import { useUser } from "@clerk/clerk-react";
import { FormEvent, useEffect, useState, type CSSProperties } from "react";
import { useApiFetch } from "../hooks/useApi";

type PlatformRole = "platform_admin" | "platform_viewer";
type PlatformUserStatus = "active" | "disabled";

interface PlatformUserRow {
  user_id: string;
  primary_email: string | null;
  role: PlatformRole;
  can_impersonate_platform: boolean;
  status: PlatformUserStatus;
  created_at: string;
  updated_at: string;
}

type ListState =
  | { status: "idle" | "loading" }
  | { status: "ok"; users: PlatformUserRow[]; bootstrapMode: boolean }
  | { status: "err"; message: string };

export function UsersPage() {
  const { user } = useUser();
  const api = useApiFetch();
  const [state, setState] = useState<ListState>({ status: "idle" });
  const [form, setForm] = useState({
    userId: "",
    primaryEmail: "",
    role: "platform_viewer" as PlatformRole,
    canImpersonate: false,
    status: "active" as PlatformUserStatus,
  });
  const [saveState, setSaveState] = useState<{ status: "idle" | "saving" | "ok" | "err"; message?: string }>({
    status: "idle",
  });

  async function loadUsers() {
    try {
      setState({ status: "loading" });
      const res = await api("/api/v1/platform/users");
      const text = await res.text();
      if (!res.ok) {
        let detail = text;
        try {
          const j = JSON.parse(text) as { error?: string; detail?: string };
          detail = j.detail ?? j.error ?? text;
        } catch {
          // leave raw
        }
        setState({ status: "err", message: detail });
        return;
      }
      const payload = JSON.parse(text) as { users?: PlatformUserRow[]; bootstrap_mode?: boolean };
      setState({ status: "ok", users: payload.users ?? [], bootstrapMode: payload.bootstrap_mode === true });
    } catch (e) {
      setState({ status: "err", message: e instanceof Error ? e.message : String(e) });
    }
  }

  useEffect(() => {
    void loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaveState({ status: "saving" });
    try {
      const res = await api("/api/v1/platform/users", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          user_id: form.userId.trim(),
          primary_email: form.primaryEmail.trim() || null,
          role: form.role,
          can_impersonate_platform: form.canImpersonate,
          status: form.status,
        }),
      });
      const text = await res.text();
      if (!res.ok) {
        let detail = text;
        try {
          const j = JSON.parse(text) as { error?: string; detail?: string };
          detail = j.detail ?? j.error ?? text;
        } catch {
          // leave raw
        }
        setSaveState({ status: "err", message: detail });
        return;
      }
      setSaveState({ status: "ok", message: "Saved." });
      await loadUsers();
    } catch (err) {
      setSaveState({ status: "err", message: err instanceof Error ? err.message : String(err) });
    }
  }

  return (
    <div>
      <p className="sd-kicker">Access control</p>
      <h1 className="sd-h1">Users & roles</h1>
      <p className="sd-lead">
        Platform role assignments in D1 (`platform_user_roles`). Effective access also requires domain allowlist (
        <code>PLATFORM_ADMIN_EMAIL_ALLOWLIST</code>).
      </p>

      <div className="sd-main-card" style={{ marginBottom: "1rem" }}>
        <form
          onSubmit={(e) => void onSubmit(e)}
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.75rem" }}
        >
          <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem", fontSize: "0.85rem" }}>
            Clerk user ID
            <input
              value={form.userId}
              onChange={(e) => setForm((f) => ({ ...f, userId: e.target.value }))}
              required
              style={inputStyle}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem", fontSize: "0.85rem" }}>
            Primary email (optional)
            <input
              value={form.primaryEmail}
              onChange={(e) => setForm((f) => ({ ...f, primaryEmail: e.target.value }))}
              placeholder="name@cotu.uk"
              style={inputStyle}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem", fontSize: "0.85rem" }}>
            Role
            <select
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as PlatformRole }))}
              style={inputStyle}
            >
              <option value="platform_admin">platform_admin</option>
              <option value="platform_viewer">platform_viewer</option>
            </select>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem", fontSize: "0.85rem" }}>
            Status
            <select
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as PlatformUserStatus }))}
              style={inputStyle}
            >
              <option value="active">active</option>
              <option value="disabled">disabled</option>
            </select>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "0.45rem", fontSize: "0.85rem", marginTop: "1.7rem" }}>
            <input
              type="checkbox"
              checked={form.canImpersonate}
              onChange={(e) => setForm((f) => ({ ...f, canImpersonate: e.target.checked }))}
            />
            can_impersonate_platform
          </label>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "end", gap: "0.5rem" }}>
            <button
              type="button"
              style={secondaryButtonStyle}
              onClick={() => user?.id && setForm((f) => ({ ...f, userId: user.id }))}
            >
              Use my Clerk user ID
            </button>
            <button type="submit" style={buttonStyle} disabled={saveState.status === "saving"}>
              {saveState.status === "saving" ? "Saving..." : "Save role"}
            </button>
          </div>
        </form>
        {saveState.status === "err" ? (
          <p style={{ margin: "0.75rem 0 0", color: "var(--sd-danger)", fontSize: "0.85rem" }}>{saveState.message}</p>
        ) : null}
        {saveState.status === "ok" ? (
          <p style={{ margin: "0.75rem 0 0", color: "var(--sd-muted)", fontSize: "0.85rem" }}>{saveState.message}</p>
        ) : null}
      </div>

      <div className="sd-main-card" style={{ padding: 0, overflow: "hidden" }}>
        {state.status === "loading" || state.status === "idle" ? (
          <div style={{ padding: "1rem 1.25rem", color: "var(--sd-muted)" }}>Loading…</div>
        ) : null}
        {state.status === "err" ? (
          <div style={{ padding: "1rem 1.25rem", color: "var(--sd-danger)" }}>{state.message}</div>
        ) : null}
        {state.status === "ok" ? (
          <>
            {state.bootstrapMode ? (
              <div style={{ padding: "0.9rem 1.25rem", fontSize: "0.85rem", color: "var(--sd-muted)" }}>
                Bootstrap mode active: no active `platform_admin` row exists yet, so allowlisted users are temporarily
                accepted. Add at least one active `platform_admin` assignment to enforce DB role checks.
              </div>
            ) : null}
            {state.users.length === 0 ? (
              <div style={{ padding: "1rem 1.25rem", color: "var(--sd-muted)" }}>No platform users assigned yet.</div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
                <thead>
                  <tr style={{ textAlign: "left", borderBottom: "1px solid var(--sd-border)", color: "var(--sd-muted)" }}>
                    <th style={thStyle}>User ID</th>
                    <th style={thStyle}>Email</th>
                    <th style={thStyle}>Role</th>
                    <th style={thStyle}>Impersonate</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {state.users.map((u) => (
                    <tr key={u.user_id} style={{ borderBottom: "1px solid var(--sd-border)" }}>
                      <td style={tdMonoStyle}>{u.user_id}</td>
                      <td style={tdStyle}>{u.primary_email ?? "—"}</td>
                      <td style={tdStyle}>{u.role}</td>
                      <td style={tdStyle}>{u.can_impersonate_platform ? "yes" : "no"}</td>
                      <td style={tdStyle}>{u.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}

const inputStyle: CSSProperties = {
  background: "var(--sd-surface)",
  border: "1px solid var(--sd-border)",
  color: "var(--sd-text)",
  borderRadius: "8px",
  padding: "0.5rem 0.6rem",
  font: "inherit",
};

const buttonStyle: CSSProperties = {
  background: "var(--sd-accent)",
  color: "#04100e",
  border: "none",
  borderRadius: "8px",
  padding: "0.55rem 0.9rem",
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};

const secondaryButtonStyle: CSSProperties = {
  ...buttonStyle,
  background: "transparent",
  color: "var(--sd-text)",
  border: "1px solid var(--sd-border)",
};

const thStyle: CSSProperties = { padding: "0.7rem 1rem", fontWeight: 600 };
const tdStyle: CSSProperties = { padding: "0.65rem 1rem" };
const tdMonoStyle: CSSProperties = {
  padding: "0.65rem 1rem",
  fontFamily: "var(--sd-mono)",
  fontSize: "0.8rem",
};
